import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { CalendarIcon, ChevronDownIcon, XMarkIcon } from '@heroicons/react/24/outline';
import CalendarPanel from './CalendarPanel';
import {
  formatUk,
  IsoDate,
  isValidIso,
  parseIsoToLocal,
  isBeforeDay,
} from './dateUtils';
import {
  DateDropdownAlign,
  DateDropdownSize,
} from './DateDropdown';
import { useUniqueId } from './useUniqueId';

export interface DateRangePresetSpec {
  label: string;
  start: IsoDate;
  end: IsoDate;
}

export interface DateRangeValue {
  start: IsoDate | null;
  end: IsoDate | null;
}

export interface DateRangeDropdownProps {
  value: DateRangeValue;
  onChange: (next: DateRangeValue) => void;
  placeholder?: string;
  label?: string;
  min?: IsoDate;
  max?: IsoDate;
  disabled?: boolean;
  clearable?: boolean;
  size?: DateDropdownSize;
  align?: DateDropdownAlign;
  /** Optional preset shortcuts rendered above the calendar. */
  presets?: DateRangePresetSpec[];
  id?: string;
  className?: string;
  'data-testid'?: string;
}

function classes(
  size: DateDropdownSize,
  open: boolean,
  disabled: boolean,
  empty: boolean,
  align: DateDropdownAlign,
  className?: string,
): string {
  return [
    'kk-date-dropdown',
    'kk-date-dropdown--range',
    `kk-date-dropdown--${size}`,
    `kk-date-dropdown--align-${align}`,
    open ? 'kk-date-dropdown--open' : null,
    disabled ? 'kk-date-dropdown--disabled' : null,
    empty ? 'kk-date-dropdown--empty' : null,
    className,
  ]
    .filter(Boolean)
    .join(' ');
}

function rangeLabel(
  start: IsoDate | null,
  end: IsoDate | null,
  placeholder: string,
): string {
  if (!start && !end) return placeholder;
  if (start && !end) return `${formatUk(start)} – …`;
  if (!start && end) return `… – ${formatUk(end)}`;
  return `${formatUk(start!)} – ${formatUk(end!)}`;
}

/**
 * Two-clicks-to-set range picker:
 *   1st click → sets start, clears end. Subsequent clicks <= start swap to start.
 *   2nd click → sets end (>= current start), closes popover.
 *
 * Optional presets render above the calendar; clicking one applies both ends and closes.
 */
export default function DateRangeDropdown({
  value,
  onChange,
  placeholder = 'Pick a range',
  label,
  min,
  max,
  disabled = false,
  clearable = true,
  size = 'md',
  align = 'start',
  presets,
  id,
  className,
  'data-testid': dataTestId,
}: DateRangeDropdownProps): JSX.Element {
  const start = isValidIso(value.start) ? value.start : null;
  const end = isValidIso(value.end) ? value.end : null;
  const empty = start === null && end === null;

  const [open, setOpen] = useState(false);
  const [hover, setHover] = useState<IsoDate | null>(null);
  // Local in-progress range so we don't fire onChange with a half-state. Only
  // commit to the parent when both ends are known (or when clearing).
  const [draftStart, setDraftStart] = useState<IsoDate | null>(start);
  const [draftEnd, setDraftEnd] = useState<IsoDate | null>(end);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const reactId = useUniqueId('date-range-dropdown');
  const popoverId = `${id ?? reactId}-popover`;

  // Keep draft in sync with external value when the dropdown is closed.
  useEffect(() => {
    if (!open) {
      setDraftStart(start);
      setDraftEnd(end);
    }
  }, [open, start, end]);

  useEffect(() => {
    if (!open) return;
    function onDocClick(e: MouseEvent) {
      if (!containerRef.current) return;
      if (containerRef.current.contains(e.target as Node)) return;
      setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === 'Escape') {
        setOpen(false);
        triggerRef.current?.focus();
      }
    }
    document.addEventListener('mousedown', onDocClick);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocClick);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useLayoutEffect(() => {
    if (!open || !containerRef.current) return;
    const grid = containerRef.current.querySelector<HTMLDivElement>(
      '.kk-date-panel__grid',
    );
    grid?.focus();
  }, [open]);

  function toggle() {
    if (disabled) return;
    setOpen((o) => !o);
  }

  function clear(e: React.MouseEvent) {
    e.stopPropagation();
    if (disabled) return;
    setDraftStart(null);
    setDraftEnd(null);
    onChange({ start: null, end: null });
  }

  function pick(iso: IsoDate) {
    // No start yet → set start.
    if (!draftStart) {
      setDraftStart(iso);
      setDraftEnd(null);
      return;
    }
    // Start exists, no end yet.
    if (!draftEnd) {
      const startD = parseIsoToLocal(draftStart);
      const pickedD = parseIsoToLocal(iso);
      if (isBeforeDay(pickedD, startD)) {
        // Clicked earlier than start → restart range from new pick.
        setDraftStart(iso);
        setDraftEnd(null);
        return;
      }
      setDraftEnd(iso);
      onChange({ start: draftStart, end: iso });
      setOpen(false);
      triggerRef.current?.focus();
      return;
    }
    // Both ends set → start a new range from this click.
    setDraftStart(iso);
    setDraftEnd(null);
  }

  function applyPreset(p: DateRangePresetSpec) {
    setDraftStart(p.start);
    setDraftEnd(p.end);
    onChange({ start: p.start, end: p.end });
    setOpen(false);
    triggerRef.current?.focus();
  }

  const visibleLabel = useMemo(
    () => rangeLabel(start, end, placeholder),
    [start, end, placeholder],
  );

  return (
    <div
      ref={containerRef}
      className={classes(size, open, disabled, empty, align, className)}
      data-testid={dataTestId}
    >
      <button
        ref={triggerRef}
        type="button"
        id={id}
        className="kk-date-dropdown__trigger"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-controls={popoverId}
        aria-label={label}
      >
        <CalendarIcon
          className="kk-date-dropdown__lead-icon"
          width={16}
          height={16}
          aria-hidden="true"
        />
        <span className="kk-date-dropdown__value">{visibleLabel}</span>
        {clearable && !empty && !disabled && (
          <span
            role="button"
            tabIndex={0}
            className="kk-date-dropdown__clear"
            aria-label="Clear range"
            onClick={clear}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                e.stopPropagation();
                onChange({ start: null, end: null });
              }
            }}
          >
            <XMarkIcon width={14} height={14} aria-hidden="true" />
          </span>
        )}
        <ChevronDownIcon
          className="kk-date-dropdown__chevron"
          width={16}
          height={16}
          aria-hidden="true"
        />
      </button>

      {open && (
        <div
          className="kk-date-dropdown__popover kk-date-dropdown__popover--range"
          id={popoverId}
          role="dialog"
          aria-label={label ?? 'Pick a date range'}
        >
          {presets && presets.length > 0 && (
            <div className="kk-date-dropdown__presets" role="group" aria-label="Date range presets">
              {presets.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  className="kk-date-dropdown__preset"
                  onClick={() => applyPreset(p)}
                >
                  {p.label}
                </button>
              ))}
            </div>
          )}
          <CalendarPanel
            rangeStartIso={draftStart}
            rangeEndIso={draftEnd}
            minIso={min}
            maxIso={max}
            onPick={pick}
            hoverIso={hover}
            onHoverChange={setHover}
            ariaLabel={label ?? 'Pick a date range'}
          />
        </div>
      )}
    </div>
  );
}
